<template>
  <div v-click-outside="() => toggleDropdown(true)" class="el-input-dropdown no-highlight" :class="{'--opened': isDropdownOpen, '--outlined': outlined !== false}" @click="toggleDropdown()">
    <label v-if="label" class="__dropdown-label">{{ label }}</label>
    <div class="__input-dropdown-inner">
      <div class="__selection">
        {{ selectedLabel }}
      </div>
      <div class="__inner-icon">
        <img :src="downArrowIcon" alt="Down arrow" data-not-lazy width="14" height="14">
      </div>
    </div>

    <div class="__dropdown-items-wrapper">
      <ul class="__dropdown-items">
        <li v-for="(item, index) in items" :key="`item-${index}`" :class="{'--selected': modelValue.length && modelValue.includes(item.value)}" @click="updateSelectedItem(item)">
          {{ item.label }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, ComputedRef, PropType, Ref} from 'vue';
import {DropdownItem} from '~/@types/common';
import vClickOutside from '~/composables/clickOutside';
import downArrowIcon from '~/assets/images/icons/down-arrow.webp';

// Data
const isDropdownOpen: Ref<boolean> = ref(false);

// PROPS
const props = defineProps({
  items: {
    type: Object as PropType<DropdownItem[]>,
    required: true,
  },
  returnFullObject: {
    type: Boolean,
    default: false,
  },
  outlined: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: null,
  },
  modelValue: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  isMultiple: {
    type: Boolean,
    default: false,
  },
});

// EMIT Definitions
const emit = defineEmits<{
  (event: 'update:modelValue', payload: string[]): void;
  (event: 'change'): void;
}>();

/**
 * Return value or full item object
 */
const selectedLabel: ComputedRef<string> = computed(() => {
  if (props.modelValue && props.modelValue.length === 1) {
    return props.items.find((item: DropdownItem) => item.value === props.modelValue[0])?.label;
  } else if (props.modelValue && props.modelValue.length > 1) {
    return props.modelValue.length + ' selected';
  } else {
    return 'any';
  }
});

/**
 * Handle item clicked event
 *
 * @param clickedItem
 */
function updateSelectedItem (clickedItem: DropdownItem) {
  if (props.modelValue?.find((item: string) => item === clickedItem.value)) {
    emit('update:modelValue', props.modelValue.filter((item: string) => item !== clickedItem.value));
  } else {
    emit('update:modelValue', [...props.modelValue, clickedItem.value as string]);
  }
  emit('change');
}

/**
 * Toggle dropdown
 */
function toggleDropdown (forcedToClose = false) {
  if (forcedToClose) {
    isDropdownOpen.value = false;
  } else {
    isDropdownOpen.value = !isDropdownOpen.value;
  }
}

</script>

<style lang="scss" scoped>
.el-input-dropdown {
  width: 100%;
  min-height: 50px;
  height: fit-content;
  position: relative;
  padding-bottom: 12px;

  .__input-dropdown-inner {
    width: 100%;
    min-height: 45px;
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 12px;
    cursor: pointer;

    .__selection {
      color: rgba(var(--text-lighter-1), 0.8);
      white-space: nowrap;
      padding-right: 12px;
    }

    .__inner-icon {
      transform: rotate(0deg);
      transform-origin: center center;
      transition: all 0.2s ease-in;

      img {
        width: 14px;
        height: auto;
      }
    }

    &:hover {
      .__selection {
        color: rgba(var(--text-lighter-1), 1);
      }
    }
  }

  .__dropdown-items-wrapper {
    max-height: 0;
    height: fit-content;
    min-width: 100%;
    overflow: hidden;
    background: rgb(var(--background-light));
    border-bottom-right-radius: 8px;
    border-bottom-left-radius: 8px;
    margin-top: 0;
    box-shadow: 0 0 8px -2px #b4b4b4;
    position: absolute;
    width: fit-content;
    z-index: 999;

    .__dropdown-items {
      padding: 8px 0;

      li {
        padding: 15px 12px;
        cursor: pointer;
        white-space: nowrap;
        background-color: #fff;
        font-weight: 300;
        transition: background-color 0.2s ease-in;
        display: flex;
        align-items: center;

        &.--selected {
          background-color: rgba(92, 82, 198, 0.2) !important;
          font-weight: 400;
        }

        input {
          margin-right: 12px;
        }

        &:hover {
          background-color: #f1f1f1;
          transition: background-color 0.2s ease-in;
        }
      }
    }
  }

  &.--opened {
    .__input-dropdown-inner {
      .__inner-icon {
        transform: rotate(180deg);
        transition: all 0.2s ease-in;
      }
    }

    .__dropdown-items-wrapper {
      max-height: 218px;
      overflow: auto;
      transition: max-height 0.4s cubic-bezier(.79,.14,.15,.86);
    }
  }

  &.--outlined {
    .__input-dropdown-inner {
      border: 1px solid rgba(var(--text-lighter-1), 0.3);
      border-radius: 5px;
    }
  }

  .__dropdown-label {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
    display: block;
    color: rgb(var(--text-default));
  }
}
</style>
